function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
import _extends from "@babel/runtime/helpers/esm/extends";
import { scaleBand, scalePoint, scaleTime } from '@mui/x-charts-vendor/d3-scale';
import { isBandScaleConfig, isPointScaleConfig } from '../../models/axis';
import { getColorScale, getOrdinalColorScale } from '../../internals/colorScale';
import { getTickNumber } from '../../hooks/useTicks';
import { getScale } from '../../internals/getScale';
import { zoomScaleRange } from './zoom';
import { getAxisExtremum } from './getAxisExtremum';
const getRange = (drawingArea, axisDirection, isReverse) => {
  const range = axisDirection === 'x' ? [drawingArea.left, drawingArea.left + drawingArea.width] : [drawingArea.top + drawingArea.height, drawingArea.top];
  return isReverse ? range.reverse() : range;
};
const isDateData = data => (data === null || data === void 0 ? void 0 : data[0]) instanceof Date;
function createDateFormatter(axis, range) {
  const timeScale = scaleTime(axis.data, range);
  return (v, _ref) => {
    let location = _ref.location;
    return location === 'tick' ? timeScale.tickFormat(axis.tickNumber)(v) : `${v.toLocaleString()}`;
  };
}
const DEFAULT_CATEGORY_GAP_RATIO = 0.2;
const DEFAULT_BAR_GAP_RATIO = 0.1;
export function computeValue(_ref2) {
  let drawingArea = _ref2.drawingArea,
    formattedSeries = _ref2.formattedSeries,
    allAxis = _ref2.axis,
    extremumGetters = _ref2.extremumGetters,
    axisDirection = _ref2.axisDirection,
    zoomData = _ref2.zoomData,
    zoomOptions = _ref2.zoomOptions,
    getFilters = _ref2.getFilters;
  const completeAxis = {};
  allAxis.forEach((eachAxis, axisIndex) => {
    var _axis$data, _axis$scaleType, _axis$min, _axis$max, _axis$min2, _axis$max2;
    const axis = eachAxis;
    const isDefaultAxis = axisIndex === 0;
    const zoomOption = zoomOptions === null || zoomOptions === void 0 ? void 0 : zoomOptions[axis.id];
    const zoom = zoomData === null || zoomData === void 0 ? void 0 : zoomData.find(_ref3 => {
      let axisId = _ref3.axisId;
      return axisId === axis.id;
    });
    const zoomRange = zoom ? [zoom.start, zoom.end] : [0, 100];
    const range = getRange(drawingArea, axisDirection, axis.reverse);
    const _getAxisExtremum = getAxisExtremum(axis, extremumGetters, isDefaultAxis, formattedSeries, zoom === undefined && !zoomOption ? getFilters : undefined // Do not apply filtering if zoom is already defined.
      ),
      _getAxisExtremum2 = _slicedToArray(_getAxisExtremum, 2),
      minData = _getAxisExtremum2[0],
      maxData = _getAxisExtremum2[1];
    const data = (_axis$data = axis.data) !== null && _axis$data !== void 0 ? _axis$data : [];
    if (isBandScaleConfig(axis)) {
      var _axis$categoryGapRati, _axis$barGapRatio;
      const categoryGapRatio = (_axis$categoryGapRati = axis.categoryGapRatio) !== null && _axis$categoryGapRati !== void 0 ? _axis$categoryGapRati : DEFAULT_CATEGORY_GAP_RATIO;
      const barGapRatio = (_axis$barGapRatio = axis.barGapRatio) !== null && _axis$barGapRatio !== void 0 ? _axis$barGapRatio : DEFAULT_BAR_GAP_RATIO;
      // Reverse range because ordinal scales are presented from top to bottom on y-axis
      const scaleRange = axisDirection === 'x' ? range : [range[1], range[0]];
      const zoomedRange = zoomScaleRange(scaleRange, zoomRange);
      completeAxis[axis.id] = _extends({
        categoryGapRatio,
        barGapRatio
      }, axis, {
        data,
        scale: scaleBand(axis.data, zoomedRange).paddingInner(categoryGapRatio).paddingOuter(categoryGapRatio / 2),
        tickNumber: axis.data.length,
        colorScale: axis.colorMap && (axis.colorMap.type === 'ordinal' ? getOrdinalColorScale(_extends({
          values: axis.data
        }, axis.colorMap)) : getColorScale(axis.colorMap))
      });
      if (isDateData(axis.data)) {
        var _axis$valueFormatter;
        const dateFormatter = createDateFormatter(axis, scaleRange);
        completeAxis[axis.id].valueFormatter = (_axis$valueFormatter = axis.valueFormatter) !== null && _axis$valueFormatter !== void 0 ? _axis$valueFormatter : dateFormatter;
      }
    }
    if (isPointScaleConfig(axis)) {
      const scaleRange = axisDirection === 'x' ? range : [...range].reverse();
      const zoomedRange = zoomScaleRange(scaleRange, zoomRange);
      completeAxis[axis.id] = _extends({}, axis, {
        data,
        scale: scalePoint(axis.data, zoomedRange),
        tickNumber: axis.data.length,
        colorScale: axis.colorMap && (axis.colorMap.type === 'ordinal' ? getOrdinalColorScale(_extends({
          values: axis.data
        }, axis.colorMap)) : getColorScale(axis.colorMap))
      });
      if (isDateData(axis.data)) {
        var _axis$valueFormatter2;
        const dateFormatter = createDateFormatter(axis, scaleRange);
        completeAxis[axis.id].valueFormatter = (_axis$valueFormatter2 = axis.valueFormatter) !== null && _axis$valueFormatter2 !== void 0 ? _axis$valueFormatter2 : dateFormatter;
      }
    }
    if (axis.scaleType === 'band' || axis.scaleType === 'point') {
      // Could be merged with the two previous "if conditions" but then TS does not get that `axis.scaleType` can't be `band` or `point`.
      return;
    }
    const scaleType = (_axis$scaleType = axis.scaleType) !== null && _axis$scaleType !== void 0 ? _axis$scaleType : 'linear';
    const axisExtremums = [(_axis$min = axis.min) !== null && _axis$min !== void 0 ? _axis$min : minData, (_axis$max = axis.max) !== null && _axis$max !== void 0 ? _axis$max : maxData];
    const rawTickNumber = getTickNumber(_extends({}, axis, {
      range,
      domain: axisExtremums
    }));
    const tickNumber = rawTickNumber / ((zoomRange[1] - zoomRange[0]) / 100);
    const zoomedRange = zoomScaleRange(range, zoomRange);

    // TODO: move nice to prop? Disable when there is zoom?
    const scale = getScale(scaleType, axisExtremums, zoomedRange).nice(rawTickNumber);
    const _scale$domain = scale.domain(),
      _scale$domain2 = _slicedToArray(_scale$domain, 2),
      minDomain = _scale$domain2[0],
      maxDomain = _scale$domain2[1];
    const domain = [(_axis$min2 = axis.min) !== null && _axis$min2 !== void 0 ? _axis$min2 : minDomain, (_axis$max2 = axis.max) !== null && _axis$max2 !== void 0 ? _axis$max2 : maxDomain];
    completeAxis[axis.id] = _extends({}, axis, {
      data,
      scaleType: scaleType,
      scale: scale.domain(domain),
      tickNumber,
      colorScale: axis.colorMap && getColorScale(axis.colorMap)
    });
  });
  return {
    axis: completeAxis,
    axisIds: allAxis.map(_ref4 => {
      let id = _ref4.id;
      return id;
    })
  };
}